import { TFunction } from 'i18next';
import { GenericResponse } from 'src/js/model/api/response/generic-response';
import { isIpv4, isIpv4OrEmpty, isMulticast, isNotIpv4Multicast } from 'src/js/validator/ip-validator-helper';
import { port } from 'src/js/validator/validator';
import * as yup from 'yup';

export enum EssenceState {
  STOPPED = 0,
  RUNNING,
  FAILED,
}

export enum EssenceType {
  video = 'video',
  audio = 'audio',
  data = 'data',
}

export interface Essence {
  address: string;
  autoStart?: boolean;
  id: number;
  inputChannel: number;
  inputFormat?: string;
  name?: string;
  numOfChannels?: number;
  packetTime?: number;
  packetTimeActual?: number;
  port: number;
  rtpPayload: number;
  sourceAddress?: string;
  type: EssenceType;
}

export interface EssenceStats {
  detectedVideoFormat?: string;
  lastReset: string;
  rxDiscardedPackets: string;
  rxLostPackets: string;
  rxPackets: string;
  state: EssenceState;
  stateString: string;
  streamSync: boolean;
  videoFormatDetectionState?: string;
}

export const essenceIsStopped = (state: EssenceState): boolean =>
  state === EssenceState.STOPPED || state === EssenceState.FAILED;
export const essenceIsStarted = (state: EssenceState): boolean =>
  state === EssenceState.RUNNING || state === EssenceState.FAILED;

export const essenceTypeToString = (type: string, t: TFunction): string => {
  switch (type) {
    case EssenceType.video:
      return 'Video';
    case EssenceType.audio:
      return 'Audio';
    case EssenceType.data:
      return t('essence.settings.ancData');
    default:
      return '';
  }
};

export const essenceStateToIcon = (state: EssenceState, streamSync: boolean): string => {
  switch (state) {
    case EssenceState.STOPPED:
      return 'StatusStopped';
    case EssenceState.RUNNING:
      return streamSync ? 'StatusOK' : 'StatusStreamWarning';
    case EssenceState.FAILED:
      return 'StatusError';
  }
};

export const essenceStateToName = (state: EssenceState, t: TFunction): string => {
  switch (state) {
    case EssenceState.STOPPED:
      return t('essence.states.stopped');
    case EssenceState.RUNNING:
      return t('essence.states.running');
    case EssenceState.FAILED:
      return t('essence.states.failed');
  }
};

const essenceStateToSortable = (state: EssenceState, streamSync: boolean): number => {
  switch (state) {
    case EssenceState.RUNNING:
      return streamSync ? 1 : 2;
    case EssenceState.STOPPED:
      return 3;
    case EssenceState.FAILED:
      return 4;
  }
};

export const essenceStateToColor = (state: EssenceState, streamSync: boolean): string => {
  switch (state) {
    case EssenceState.STOPPED:
      return 'haiui-gray-07';
    case EssenceState.RUNNING:
      return streamSync ? 'haiui-green-01' : 'haiui-amber-01';
    case EssenceState.FAILED:
      return 'haiui-red-01';
  }
};

export const essenceStateToStatus = (state: EssenceState, streamSync: boolean): string => {
  switch (state) {
    case EssenceState.STOPPED:
      return 'INACTIVE';
    case EssenceState.RUNNING:
      return streamSync ? 'ACTIVE' : 'ALERT';
    case EssenceState.FAILED:
      return 'FAILED';
  }
};

export const essenceModelToStatus = (model: EssenceViewModel): string => {
  return essenceStateToStatus(model.state, model.stats.streamSync);
};

export interface EssenceResponse extends GenericResponse {
  info: Essence;
  stats: EssenceStats;
  warning?: string;
}

export interface GetAllEssencesResponse {
  data: EssenceResponse[];
}

export interface EssenceViewModel extends Essence {
  stats?: EssenceStats;
  state: EssenceState;

  stateSortable: number;
  addressTxt: string;
  typeTxt: string;

  status: string;

  selected: boolean /* checked by checkbox */;
  hidden: boolean /* when filtered out */;
  updated: string /* to trigger setState to detect change and re-render */;
}

export const mapEssenceViewModel = (response: EssenceResponse, t: TFunction): EssenceViewModel => ({
  ...response.info,
  stats: { ...response.stats },
  inputFormat: response.info.inputFormat || '0',
  numOfChannels: response.info.numOfChannels || 2,
  packetTime: response.info.packetTime || 1,
  packetTimeActual: response.info.packetTimeActual,
  state: response.stats.state,

  stateSortable: essenceStateToSortable(response.stats.state, response.stats.streamSync),
  addressTxt: `${response.info.address}:${response.info.port}`,
  typeTxt: essenceTypeToString(response.info.type, t),

  status: essenceStateToStatus(response.stats.state, response.stats.streamSync), // required for ActionBar
  selected: false,
  hidden: false,
  updated: new Date().toISOString(),
});

export const mapEssencesResponse = (response: GetAllEssencesResponse, t: TFunction): EssenceViewModel[] => {
  const mapped = response?.data.map((item) => mapEssenceViewModel(item, t));
  return mapped;
};

export const mapEssenceViewModelToRequest = (input: EssenceViewModel): Essence => {
  return { ...input, port: input.port != null ? Number(input.port) : input.port };
};

export const newEssenceViewModel = (): EssenceViewModel => ({
  address: '',
  id: -1,
  autoStart: true,
  inputChannel: 0,
  inputFormat: '0',
  name: '',
  numOfChannels: 2,
  packetTime: 1,
  port: undefined,
  rtpPayload: 96,
  sourceAddress: '',
  type: EssenceType.video,

  state: 0,
  stateSortable: EssenceState.STOPPED,
  addressTxt: '',
  typeTxt: '',
  status: 'INACTIVE',
  selected: false,
  hidden: false,
  updated: new Date().toISOString(),
});

export interface ResolutionViewModel {
  value: string;
  label: string;
}

export interface ResolutionResponse extends GenericResponse {
  value: string;
  name: string;
}

export interface GetAllResolutionsResponse {
  resolutions: ResolutionResponse[];
}

const mapResolutionViewModel = (response: ResolutionResponse): ResolutionViewModel => {
  return { value: response.value, label: response.name };
};

export const mapResolutionResponse = (response: GetAllResolutionsResponse): ResolutionViewModel[] => {
  return response.resolutions?.map((item) => mapResolutionViewModel(item));
};

/*
 * Validation
 */

const isMultiCast = (value: string) => {
  if (value === '0.0.0.0') {
    return true;
  }

  return isMulticast(value);
};

export const essenceValidationSchema = (t: TFunction) =>
  yup.object<EssenceViewModel>().shape({
    name: yup.string().max(63, t('validation.maxLength', { length: 63 })),
    numOfChannels: yup.number().when('type', {
      is: 'audio',
      then: yup
        .number()
        .required(t('validation.required'))
        .typeError(t('validation.number'))
        .min(1, t('validation.essence.numOfChannels'))
        .max(16, t('validation.essence.numOfChannels')),
    }),
    address: yup
      .string()
      .required(t('validation.required'))
      .test('is-multicast', t('validation.multicast'), (value) => isMultiCast(value)),
    port: port(t).required(t('validation.required')),
    rtpPayload: yup
      .number()
      .typeError(t('validation.number'))
      .min(0, t('validation.essence.rtpPayload'))
      .max(127, t('validation.essence.rtpPayload')),
    sourceAddress: yup
      .string()
      .test('is-ipv4-or-empty', t('validation.ipv4'), (value) => isIpv4OrEmpty(value))
      .test('is-ipv4-not-multicast', t('validation.sourceAddressCannotBeMulticast'), (value) =>
        isNotIpv4Multicast(value),
      )
      .when('address', {
        is: (address: string) => typeof address === 'string' && address.trim() === '0.0.0.0',
        then: yup
          .string()
          .required(t('essence.validation.sourceAddressIsRequiredForUnicastSource'))
          .test('is-ipv4', t('validation.ipv4'), (value) => isIpv4(value)),
      }),
  });
